import React, { useState, useEffect, useMemo } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  Alert,
  ActivityIndicator,
  Image,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import Icon from 'react-native-vector-icons/Feather';
import { useProfile } from '../hooks/useProfile';
import { Colors, Typography, Spacing, Radius, GlobalStyles } from '../theme';
import { DEFAULT_AVATAR } from '../firebase';
import type { RootStackParamList } from '../types';
import TonyteBackground from '../components/TonyteBackground';
import PickerField from '../components/PickerField';
import { pickImageFromLibrary } from '../utils/permissions';
import TonyteCropModal from '../components/TonyteCropModal';
import { BUILT_IN_INTEREST_KEYS, getInterestCategoryTranslationKey, getInterestTranslationKey, INTEREST_CATEGORIES } from '../data/interests';
import { canAccessCity, normalizeCityKey } from '../utils/cities';
import { MIN_AGE, isDateOfBirthValid } from '../utils/age';
import { useI18n } from '../i18n';
import LoadingSpinner from '../components/LoadingSpinner';
import { useTheme } from '../theme/useTheme';
import { daylightStyle } from '../theme/daylightStyle';
import type { TonyteTheme } from '../theme/themes';

type Props = NativeStackScreenProps<RootStackParamList, 'EditProfile'>;

const EditProfileScreen: React.FC<Props> = ({ navigation }) => {
  const insets = useSafeAreaInsets();
  const { t } = useI18n();
  const { theme } = useTheme();
  const appearance = useMemo(() => createThemeStyles(theme), [theme]);
  const { profile, loading, updateProfile } = useProfile(null);

  const [name, setName] = useState('');
  const [bio, setBio] = useState('');
  const [city, setCity] = useState('');
  const [country, setCountry] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [photo, setPhoto] = useState(DEFAULT_AVATAR);
  const [cropUri, setCropUri] = useState('');
  const [showCrop, setShowCrop] = useState(false);
  const [selectedInterests, setSelectedInterests] = useState<string[]>([]);
  const [interestSearchQuery, setInterestSearchQuery] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (profile) {
      setName(profile.name ?? '');
      setBio(profile.bio ?? '');
      setCity(profile.city ?? '');
      setCountry(profile.country ?? '');
      setDateOfBirth(profile.dateOfBirth ?? '');
      setPhoto(profile.photo ?? DEFAULT_AVATAR);
      setSelectedInterests([...new Set((profile.interests ?? []).map(i => i.trim()).filter(Boolean))]);
    }
  }, [profile]);

  const normalizedInterestSearch = interestSearchQuery.trim().replace(/\s+/g, ' ');
  const hasExactInterestMatch = normalizedInterestSearch
    ? [...INTEREST_CATEGORIES.flatMap(category => category.interests), ...selectedInterests]
      .some(interest => interest.toLowerCase() === normalizedInterestSearch.toLowerCase())
    : true;
  const filteredInterestCategories = useMemo(() => {
    const queryText = interestSearchQuery.trim().toLowerCase();
    return INTEREST_CATEGORIES
      .map(category => ({
        ...category,
        interests: category.interests.filter(interest => (
          interest.toLowerCase().includes(queryText)
          || t(getInterestTranslationKey(interest)).toLowerCase().includes(queryText)
        )),
      }))
      .filter(category => !queryText || category.interests.length > 0);
  }, [interestSearchQuery, t]);

  const pickPhoto = async () => {
    const result = await pickImageFromLibrary({ mediaType: 'photo', quality: 1 });
    if (result?.assets?.[0]?.uri) {
      console.log('[Tonyte][ImagePreview] selected image', result.assets[0].uri);
      setCropUri(result.assets[0].uri);
      setShowCrop(true);
      console.log('[Tonyte][ImagePreview] opening preview', {
        pendingImageType: 'profile',
        imagePreviewVisible: true,
      });
      return;
    }
  };

  const handleCropDone = (croppedUri: string) => {
    console.log('[Tonyte][ImagePreview] modal closed by confirm', {
      pendingImageType: 'profile',
      uri: croppedUri,
    });
    setPhoto(croppedUri);
    setShowCrop(false);
    setCropUri('');
  };

  const cancelImagePreview = () => {
    console.log('[Tonyte][ImagePreview] modal closed by cancel', {
      pendingImageType: 'profile',
      pendingImageUri: cropUri || null,
    });
    setShowCrop(false);
    setCropUri('');
  };

  const toggleInterest = (interest: string) => {
    setSelectedInterests(prev => {
      if (prev.includes(interest)) return prev.filter(i => i !== interest);
      if (prev.length >= 30) return prev;
      return [...prev, interest];
    });
  };

  const clearSelectedInterests = () => {
    setSelectedInterests([]);
    setInterestSearchQuery('');
  };

  const addCustomInterest = () => {
    if (!normalizedInterestSearch || hasExactInterestMatch) return;
    setSelectedInterests(prev =>
      prev.includes(normalizedInterestSearch) || prev.length >= 30 ? prev : [...prev, normalizedInterestSearch],
    );
    setInterestSearchQuery('');
  };

  const save = async () => {
    if (!name.trim()) {
      Alert.alert(t('editProfile.nameRequired'), t('editProfile.addName'));
      return;
    }
    if (dateOfBirth && !isDateOfBirthValid(dateOfBirth)) {
      Alert.alert(t('editProfile.invalidDob'), t('editProfile.ageRequirement').replace('{age}', String(MIN_AGE)));
      return;
    }

    setSaving(true);
    try {
      const nextCustomInterests = selectedInterests.filter(interest => !BUILT_IN_INTEREST_KEYS.has(interest.trim().toLowerCase()));
      const safeCity = canAccessCity(normalizeCityKey(city), profile?.role) ? city.trim() : 'Dresden';
      await updateProfile({
        name: name.trim(),
        username: name.trim(),
        bio: bio.trim(),
        city: safeCity,
        locationCity: safeCity,
        country: country.trim(),
        locationCountry: country.trim(),
        locationLabel: safeCity ? `Based in ${safeCity}` : '',
        ...(dateOfBirth ? { dateOfBirth } : {}),
        photo,
        interests: selectedInterests,
        customInterests: nextCustomInterests,
      });
      navigation.goBack();
    } catch (error) {
      Alert.alert(
        t('editProfile.saveFailed'),
        error instanceof Error ? error.message : t('common.tryAgain'),
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <TonyteBackground appearanceAware style={[styles.container, appearance.container]}>
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={[styles.content, { paddingBottom: insets.bottom + Spacing.xl }]}
        keyboardShouldPersistTaps="handled"
      >
      <View style={[styles.header, { paddingTop: insets.top + Spacing.sm }]}>
        <TouchableOpacity onPress={() => navigation.goBack()}>
          <Text style={[styles.backText, appearance.accentText]}>← {t('common.back')}</Text>
        </TouchableOpacity>
        <Text style={[styles.headerTitle, appearance.textPrimary]}>{t('profile.editProfile')}</Text>
        <TouchableOpacity onPress={save} disabled={saving}>
          {saving ? (
            <ActivityIndicator size="small" color={Colors.sunset} />
          ) : (
            <Text style={[styles.saveText, appearance.accentText]}>{t('common.save')}</Text>
          )}
        </TouchableOpacity>
      </View>

      <TouchableOpacity style={styles.avatarWrapper} onPress={pickPhoto}>
        <Image source={{ uri: photo }} style={styles.avatar} />
        <View style={styles.avatarBadge}>
          <Icon name="camera" size={14} color="#fff" />
        </View>
      </TouchableOpacity>

      <Label text={t('editProfile.name')} />
      <TextInput
        style={[GlobalStyles.inputBase, appearance.input]}
        value={name}
        onChangeText={setName}
        placeholder={t('editProfile.yourName')}
        placeholderTextColor={theme.colors.textMuted}
      />

      <Label text={t('editProfile.bio')} />
      <TextInput
        style={[GlobalStyles.inputBase, styles.textarea, appearance.input]}
        value={bio}
        onChangeText={setBio}
        placeholder={t('editProfile.bioPlaceholder')}
        placeholderTextColor={theme.colors.textMuted}
        multiline
        numberOfLines={3}
      />

      <Label text={t('editProfile.city')} />
      <TextInput
        style={[GlobalStyles.inputBase, appearance.input]}
        value={city}
        onChangeText={setCity}
        placeholder={t('editProfile.yourCity')}
        placeholderTextColor={theme.colors.textMuted}
      />

      <Label text={t('editProfile.country')} />
      <TextInput
        style={[GlobalStyles.inputBase, appearance.input]}
        value={country}
        onChangeText={setCountry}
        placeholder={t('editProfile.yourCountry')}
        placeholderTextColor={theme.colors.textMuted}
      />

      <Label text={t('editProfile.dateOfBirth')} />
      <PickerField
        value={dateOfBirth}
        onChange={setDateOfBirth}
        onReject={() => Alert.alert(t('editProfile.invalidDob'), t('editProfile.ageRequirement').replace('{age}', String(MIN_AGE)))}
        placeholder={t('completeProfile.dobPlaceholder')}
        mode="date"
        maximumDate={new Date()}
        noMargin
      />

      <View style={[styles.personalityCard, appearance.card]}>
        <View style={styles.personalityHeader}>
          <View>
            <Text style={[styles.personalityTitle, appearance.textPrimary]}>{t('editProfile.personality')}</Text>
            <Text style={[styles.personalitySub, appearance.textMuted]}>{t('editProfile.selectedCount').replace('{count}', String(selectedInterests.length))}</Text>
          </View>
          {selectedInterests.length ? (
            <TouchableOpacity style={[styles.clearInterestsButton, appearance.control]} onPress={clearSelectedInterests}>
              <Text style={[styles.clearInterestsText, appearance.textSecondary]}>{t('common.clear')}</Text>
            </TouchableOpacity>
          ) : null}
        </View>

        <View style={[styles.interestSearchBox, appearance.input]}>
          <Icon name="search" size={16} color={theme.colors.iconMuted} />
          <TextInput
            value={interestSearchQuery}
            onChangeText={setInterestSearchQuery}
            placeholder={t('interests.searchInterests')}
            placeholderTextColor={theme.colors.textMuted}
            style={[styles.interestSearchInput, appearance.textPrimary]}
          />
        </View>

        {selectedInterests.length ? (
          <View style={styles.selectedInterestChips}>
            {selectedInterests.map((interest, idx) => (
              <TouchableOpacity key={`${interest}-${idx}`} style={styles.selectedInterestChip} onPress={() => toggleInterest(interest)}>
                <Text style={styles.selectedInterestChipText}>{BUILT_IN_INTEREST_KEYS.has(interest.toLowerCase()) ? t(getInterestTranslationKey(interest)) : interest}</Text>
                <Icon name="x" size={12} color="#07101d" />
              </TouchableOpacity>
            ))}
          </View>
        ) : null}

        {normalizedInterestSearch && !hasExactInterestMatch ? (
          <TouchableOpacity style={styles.addCustomInterestButton} onPress={addCustomInterest}>
            <Icon name="plus" size={15} color={Colors.sunset} />
            <Text style={styles.addCustomInterestText}>{t('editProfile.addCustom').replace('{interest}', normalizedInterestSearch)}</Text>
          </TouchableOpacity>
        ) : null}
      </View>

      {filteredInterestCategories.map(category => (
        <View key={category.name} style={[styles.interestCategoryCard, appearance.card]}>
          <Text style={[styles.interestCategoryTitle, appearance.textPrimary]}>{t(getInterestCategoryTranslationKey(category.name))}</Text>
          <View style={styles.interestCategoryGrid}>
            {category.interests.map(interest => {
              const selected = selectedInterests.includes(interest);
              return (
                <TouchableOpacity
                  key={`${category.name}-${interest}`}
                  style={[styles.interestOptionChip, appearance.control, selected && styles.interestOptionChipActive, selected && appearance.activeChip]}
                  onPress={() => toggleInterest(interest)}
                >
                  <Text style={[styles.interestOptionText, appearance.textSecondary, selected && styles.interestOptionTextActive, selected && appearance.activeText]}>{t(getInterestTranslationKey(interest))}</Text>
                </TouchableOpacity>
              );
            })}
          </View>
        </View>
      ))}
      </ScrollView>
      {showCrop && cropUri ? (
        <TonyteCropModal
          session={{ kind: 'profile', uri: cropUri, aspectRatio: '1:1' }}
          onCrop={handleCropDone}
          onCancel={cancelImagePreview}
        />
      ) : null}
    </TonyteBackground>
  );
};

const Label: React.FC<{ text: string }> = ({ text }) => {
  const { theme } = useTheme();
  return <Text style={[styles.label, daylightStyle(theme, { color: theme.colors.textSecondary })]}>{text}</Text>;
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: Colors.bg },
  scroll: { flex: 1, backgroundColor: 'transparent' },
  content: { paddingHorizontal: Spacing.base, gap: Spacing.sm },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: Spacing.md,
  },
  backText: { color: Colors.sunset, fontSize: Typography.base },
  headerTitle: {
    color: Colors.textPrimary,
    fontSize: Typography.md,
    fontWeight: Typography.bold,
  },
  saveText: { color: Colors.sunset, fontSize: Typography.base, fontWeight: Typography.semibold },
  avatarWrapper: {
    alignSelf: 'center',
    marginVertical: Spacing.lg,
    position: 'relative',
  },
  avatar: {
    width: 90,
    height: 90,
    borderRadius: 45,
    borderWidth: 3,
    borderColor: Colors.sunset,
  },
  avatarBadge: {
    position: 'absolute',
    bottom: 0,
    right: 0,
    backgroundColor: Colors.sunset,
    borderRadius: 13,
    width: 26,
    height: 26,
    alignItems: 'center',
    justifyContent: 'center',
  },
  label: {
    color: Colors.textSecondary,
    fontSize: Typography.sm,
    fontWeight: Typography.semibold,
    marginTop: Spacing.xs,
  },
  textarea: { height: 80, textAlignVertical: 'top' },
  personalityCard: {
    marginTop: Spacing.md,
    borderRadius: 28,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
    backgroundColor: 'rgba(16,23,34,0.68)',
    padding: Spacing.base,
    gap: Spacing.md,
    overflow: 'hidden',
  },
  personalityHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: Spacing.md,
  },
  personalityTitle: {
    color: Colors.textPrimary,
    fontSize: Typography.lg,
    fontWeight: Typography.bold,
  },
  personalitySub: {
    marginTop: 4,
    color: Colors.textMuted,
    fontSize: Typography.xs,
    fontWeight: Typography.semibold,
  },
  clearInterestsButton: {
    borderRadius: Radius.pill,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.12)',
    backgroundColor: 'rgba(255,255,255,0.07)',
    paddingHorizontal: Spacing.md,
    paddingVertical: 8,
  },
  clearInterestsText: {
    color: Colors.textSecondary,
    fontSize: Typography.xs,
    fontWeight: Typography.bold,
  },
  interestSearchBox: {
    minHeight: 48,
    borderRadius: 18,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
    backgroundColor: 'rgba(255,255,255,0.07)',
    paddingHorizontal: Spacing.md,
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.sm,
  },
  interestSearchInput: {
    flex: 1,
    color: Colors.textPrimary,
    fontSize: Typography.base,
    paddingVertical: 0,
  },
  selectedInterestChips: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: Spacing.sm,
  },
  selectedInterestChip: {
    minHeight: 34,
    borderRadius: Radius.pill,
    backgroundColor: Colors.turquoise,
    paddingHorizontal: Spacing.md,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  selectedInterestChipText: {
    color: '#07101d',
    fontSize: Typography.sm,
    fontWeight: Typography.bold,
  },
  addCustomInterestButton: {
    minHeight: 42,
    borderRadius: 18,
    borderWidth: 1,
    borderStyle: 'dashed',
    borderColor: 'rgba(255,122,0,0.42)',
    backgroundColor: 'rgba(255,122,0,0.08)',
    paddingHorizontal: Spacing.md,
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.sm,
  },
  addCustomInterestText: {
    color: Colors.sunset,
    fontSize: Typography.sm,
    fontWeight: Typography.bold,
  },
  interestCategoryCard: {
    borderRadius: 24,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
    backgroundColor: 'rgba(16,23,34,0.56)',
    padding: Spacing.base,
    marginTop: Spacing.md,
  },
  interestCategoryTitle: {
    color: Colors.textPrimary,
    fontSize: Typography.base,
    fontWeight: Typography.bold,
    marginBottom: Spacing.md,
  },
  interestCategoryGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: Spacing.sm,
  },
  interestOptionChip: {
    minHeight: 36,
    borderRadius: Radius.pill,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
    backgroundColor: 'rgba(255,255,255,0.08)',
    paddingHorizontal: Spacing.md,
    justifyContent: 'center',
  },
  interestOptionChipActive: {
    borderColor: 'rgba(255,122,0,0.55)',
    backgroundColor: 'rgba(255,122,0,0.16)',
  },
  interestOptionText: {
    color: Colors.textSecondary,
    fontSize: Typography.sm,
    fontWeight: Typography.medium,
  },
  interestOptionTextActive: {
    color: '#fdba74',
    fontWeight: Typography.bold,
  },
});

const createThemeStyles = (theme: TonyteTheme) => StyleSheet.create({
  container: daylightStyle(theme, { backgroundColor: theme.colors.background }),
  input: daylightStyle(theme, {
    backgroundColor: theme.colors.inputBackground,
    borderColor: theme.colors.borderStrong,
    color: theme.colors.textPrimary,
    ...theme.shadows.card,
  }),
  card: daylightStyle(theme, {
    backgroundColor: theme.colors.cardBackground,
    borderColor: theme.colors.cardBorder,
    ...theme.shadows.card,
  }),
  control: daylightStyle(theme, {
    backgroundColor: theme.colors.controlBackground,
    borderColor: theme.colors.borderStrong,
  }),
  activeChip: daylightStyle(theme, {
    backgroundColor: theme.colors.chipBackgroundActive,
    borderColor: theme.colors.accent,
  }),
  textPrimary: daylightStyle(theme, { color: theme.colors.textPrimary }),
  textSecondary: daylightStyle(theme, { color: theme.colors.textSecondary }),
  textMuted: daylightStyle(theme, { color: theme.colors.textMuted }),
  accentText: daylightStyle(theme, { color: theme.colors.accent }),
  activeText: daylightStyle(theme, { color: theme.colors.accent }),
});

export default EditProfileScreen;
